// =============================================================================
// BookingPricingManagement.tsx — club admins set hourly booking prices (FORT-288).
//
// The club's own screen, outside the Fortius settings shell: loads the current
// club's pricing and facility types and saves through the club-admin gated
// PUT /clients/current/settings/booking-pricing. The form is BookingPricingEditor.
// =============================================================================

import React, { useEffect, useState } from 'react';
import { Text } from '../../../ui/primitives/Text';
import { Stack } from '../../../ui/primitives/Stack';
import { Alert } from '../../../ui/primitives/Alert';
import { useTokens } from '../../../core/theme/TokensProvider';
import { useClientConfigService } from '../../../services/clients/ClientConfigServiceProvider';
import { useBookingService } from '../../../services/bookings/BookingServiceProvider';
import { useCurrencySymbol } from '../../../hooks/useFormatters';
import { BookingPricingSettings, DEFAULT_BOOKING_PRICING } from '../../../model/client/ClientSettings';
import { BookingPricingEditor } from './BookingPricingEditor';

const BookingPricingManagement: React.FC = () => {
  const tokens = useTokens();
  const clientConfigService = useClientConfigService();
  const bookingService = useBookingService();
  const currencySymbol = useCurrencySymbol();

  const [loaded, setLoaded] = useState<{ pricing: BookingPricingSettings; facilityTypes: string[] } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [config, facilityTypes] = await Promise.all([
          clientConfigService.getCurrentConfig(),
          bookingService.getFacilityTypes(),
        ]);
        if (cancelled) return;
        if (!config) {
          setLoadError('Failed to load booking prices.');
          return;
        }
        setLoaded({
          pricing: config.settings.bookingPricing ?? DEFAULT_BOOKING_PRICING,
          facilityTypes: (facilityTypes ?? []).map((t) => t.facilityType),
        });
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load booking prices.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientConfigService, bookingService]);

  return (
    <Stack gap={5} style={{ padding: tokens.space[6] }} data-testid="booking-pricing">
      <Text variant="h2">Booking Pricing</Text>
      {loadError && <Alert type="error" message={loadError} data-testid="booking-pricing-load-error" />}
      {!loadError && loaded && (
        <BookingPricingEditor
          facilityTypes={loaded.facilityTypes}
          pricing={loaded.pricing}
          currencySymbol={currencySymbol}
          onSave={(pricing) => clientConfigService.updateBookingPricing(pricing)}
        />
      )}
    </Stack>
  );
};

export default BookingPricingManagement;
